// @todo: move other ActionTypes here, that are currently defined in the action-files
// to have a better overview, because: ActionTypes must be unique!

import { OIDCRequestScopes } from "@modules/oidc/src"


/**
 * All existing EntityTypes, enumerated
 */
export enum EntityType {
  // #region action request related EntityTypes
  ActionRequest = "actionRequest",
  // #endregion

  // #region Program related EntityTypes
  Category = "categories",
  Program = "program",
  // #endregion

  // #region Challenge related EntityTypes
  AbstractChallenge = "challenge",
  BasicChallenge = "basicChallenge",
  Fund = "fund",
  AbstractProposal = "proposal",
  BasicProposal = "basicProposal",
  FundApplication = "fundApplication",
  AbstractProposalAttachment = "proposalAttachment",
  DocumentProposalAttachment = "documentProposalAttachment",
  FileProposalAttachment = "fileProposalAttachment",
  ProjectPlanProposalAttachment = "projectPlanProposalAttachment",
  AttachmentDefinition = "attachmentDefinition",
  // #endregion

  // #region Feedback related EntityTypes
  Discussion = "discussion",
  FeedbackInvitation = "feedbackInvitation",
  FeedbackPost = "feedbackPost",
  // #endregion

  // #region Idea related EntityTypes
  Idea = "idea",
  // #endregion

  // #region project related EntityTypes
  Project = "project",
  ProjectFollowership = "projectFollowership",
  SupportRequest = "supportRequest",
  TeamUpload = "teamUpload",
  // #endregion

  // #region document
  ProjectDocument = "projectDocument",
  Chapter = "chapter",
  FinancePlan = "financePlan",
  // #endregion

  // #region Platform related EntityTypes
  Provider = "provider",
  User = "user",
  UserObjectRole = "userObjectRole"
  // #endregion
}

// #region define endpoint function types

/**
 * Any EntityType that does not fully map the class hierarchy from the backend API,
 * but instead maps each class in the hierarchy on one single "merged" EntityType.
 * E.g.: UserObjectRole contains/covers AbstractUserProjectRole, ProjectCoordinator, ..., TenantManager, ...
 */
type MergedEntityTypeHierarchy = EntityType.UserObjectRole | EntityType.ActionRequest

/**
 * EntityTypes that are abstract, but may be loadable as collection.
 * They do not support single-entity operations like load, create, update, delete.
 */
type AbstractBaseEntityType = EntityType.AbstractChallenge | EntityType.AbstractProposal | EntityType.AbstractProposalAttachment

/*
 * // TODO How to deal with EntityTypes that are loadable, but can only be loaded via at least one SubResource endpoint?:
 * // We could create an other type like SubResourceCollectionLoadableEntityType
 * // => Can additionally be used to define the SubResourceList used in the entityTypeEndpointList
 * // => This type should be used for a new "useEntitySubResourceCollection" hook. To facilitate migration,
 * // the parentIRI property of the "useEntityCollection" hook should be marked as deprecated.
 * - EntityType.Category
 * - EntityType.Discussion
 * - EntityType.FeedbackPost
 * - EntityType.Idea
 * - EntityType.ProjectFollowership
 * - EntityType.Proposal
 * - EntityType.ProposalAttachment
 * - EntityType.Project
 * - EntityType.TeamUpload
 * - EntityType.UserObjectRole
 *
 * @link https://futureprojects.atlassian.net/browse/FCP-2041
 */

/** EntityTypes that are not loadable via collection call */
type NotCollectionLoadableEntityType = undefined
export type CollectionLoadableEntityType = Exclude<EntityType, NotCollectionLoadableEntityType>

/**
 * EntityTypes that are not loadable via individual backend API calls via the "default" API handling
 * (via src/redux/common).
 */
type NotSingleLoadableEntityType =
  AbstractBaseEntityType |
  MergedEntityTypeHierarchy |
  EntityType.AttachmentDefinition |
  EntityType.Category |
  EntityType.ProjectFollowership |
  EntityType.TeamUpload
export type SingleLoadableEntityType = Exclude<EntityType, NotSingleLoadableEntityType>

/** EntityTypes that are loadable via collection call or single call */
export type LoadableEntityType = SingleLoadableEntityType | CollectionLoadableEntityType

/*
 * // TODO How to deal with EntityTypes that are createable, but can only be created via SubResource endpoint?:
 * - EntityType.Category
 * - EntityType.Discussion
 * - EntityType.FeedbackPost
 * - EntityType.TeamUpload
 *
 * @link https://futureprojects.atlassian.net/browse/FCP-2041
 */

/** EntityTypes that are not createable */
type NotCreateableEntityType = AbstractBaseEntityType | MergedEntityTypeHierarchy
export type CreateableEntityType = Exclude<LoadableEntityType, NotCreateableEntityType>

/** EntityTypes that are not updateable */
type NotUpdateableEntityType = EntityType.ProjectFollowership | AbstractBaseEntityType | MergedEntityTypeHierarchy
export type UpdateableEntityType = Exclude<LoadableEntityType, NotUpdateableEntityType>

/**
 * Any EntityType that may ever reach the AppState.
 */
export type StoredEntityType = LoadableEntityType | CreateableEntityType | UpdateableEntityType

/** EntityTypes that are not deleteable */
type NotDeleteableEntityType = EntityType.Provider | AbstractBaseEntityType | MergedEntityTypeHierarchy
export type DeleteableEntityType = Exclude<StoredEntityType, NotDeleteableEntityType>

/**
 * Any EntityType whose CLUD backend API requests may be tracked for a single entity.
 * This contains all 4 "standard operation" entity types,
 * plus entity types in a `MergedEntityTypeHierarchy` that are CLUD'ed via specific operations.
 */
export type SingleCLUDApiRequestTrackableEntityType =
  SingleLoadableEntityType |
  CreateableEntityType |
  UpdateableEntityType |
  DeleteableEntityType |
  MergedEntityTypeHierarchy

// #endregion

/**
 * An IEntityTypeReference is an interface that references an EntityType by its typed name.
 */
export interface IEntityTypeReference<E extends EntityType> {
  entityType: E // identifier for an EntityType
}

/**
 * EntityTypes that are crippled when fetched as collection and therefor using the detailResult property.
 * Is mainly used in tests to create similar entity data as the backend does.
 *
 * Must be synchronized to the backend behaviour.
 *
 * @see backend: /doc/api.md
 */
export const DetailResultEntityTypes: EntityType[] = [
  EntityType.Project,
  EntityType.BasicChallenge,
  EntityType.Fund,
  EntityType.Program,
  EntityType.BasicProposal,
  EntityType.FundApplication,
  EntityType.User,
  EntityType.Idea,
  EntityType.Provider
]

/**
 * EntityTypes that have file properties which may be uploaded via `UploadType`.
 */
export type FileUploadEntityTypes =
  EntityType.BasicChallenge |
  EntityType.Fund |
  EntityType.Program |
  EntityType.Project |
  EntityType.User

/**
 * types that represent an option to upload a file
 *
 * to add new UploadTypes the uploadFileSaga must be extended to trigger the (re)load of depending entities
 * e.g. if a challenge logo was uploaded, the corresponding IFund/IChallenge should be (re)freshed
 */
export enum UploadType {
  BasicChallengeLogo = "basic_challenge_logo",
  FundLogo = "fund_logo",
  ProgramLogo = "program_logo",
  ProjectPicture = "project_picture",
  ProjectVisualization = "project_visualization",
  UserPicture = "user_picture",

  // uploads connected to complex EntityTypes
  // ApplicationAttachment = "application_attachment",
  // TeamUpload = "team_upload",
}

/**
 * Types for general api actions
 */
export enum GeneralApiActionTypes {
  // EntityType related actions that are sent from Application level,
  // and trigger 'generalSaga`s that update state.data (scopedEntityReducer) and allow tracking of usecase-specific request status (via entityUsecaseReducer).
  Load = "_LOAD_ENTITY_",
  LoadCollection = "_LOAD_ENTITY_COLLECTION_",
  LoadCollectionPage = "_LOAD_ENTITY_COLLECTION_PAGE_",
  Create = "_CREATE_ENTITY_",
  Update = "_UPDATE_ENTITY_",
  Delete = "_DELETE_ENTITY_",
  Upload = "_UPLOAD_FILE_",

  // model "success" actions that are used by scopedEntityReducer (for state.data), entityUsecaseReducer (to cleanup state.entityUsecases), and maybe specific reducers
  LoadSuccess = "_LOAD_SUCCESS_",
  LoadCollectionSuccess = "_LOAD_COLLECTION_SUCCESS_",
  CreateSuccess = "_CREATE_ENTITY_SUCCESS_",
  UpdateSuccess = "_UPDATE_ENTITY_SUCCESS_",
  DeleteSuccess = "_DELETE_ENTITY_SUCCESS_",

  // tracking of request state
  NewUsecaseRequestRunning = "_NEW_USECASE_RUNNING_",
  NewUsecaseRequestSuccess = "_NEW_USECASE_SUCCESS_",

  // statistics
  LoadStatistics = "_LOAD_STATISTICS_",
  LoadStatisticsRunning = "_LOAD_STATISTICS_RUNNING_",
  LoadStatisticsSuccess = "_LOAD_STATISTICS_SUCCESS_",
}

/**
 * ActionTypes are combined from diverse general and specific types of actions
 */
export const ActionTypes = { ...GeneralApiActionTypes /* , ...AndereActionTypes */ }
export type ActionType = typeof ActionTypes

/**
 * Enumeration of available ScopeTypes/ registered use cases:
 * a ScopeType defines a kind of operation, to be used on a special entity type or some action connected to an entity
 *
 * The identifiers content must match to the EntityTypes!!
 */
export enum ScopeTypes {

  /** used as default usecaseKey for uploading a file in diverse situations (logos, images, ...) */
  UploadFileOperation = "_usecase_upload_file_operation",

  // general scopetypes are created by combining EntityType + action, e.g. project + _operation

  // specific ScopeTypes
  // NOTE no pages/component listens to state.requests of this ScopeType
  // it's only here b/c activateFeedbackInvitationSaga sadly sends those messages that no-one will ever hear ...
  FeedbackInvitationOperation = "feedbackInvitation_operation",
  ProposalOperation = "proposal_operation",
  UserOperation = "user_operation", // still used for all not-yet-migrated user operations in sagas: currentUser, registration, users
  VerificationOperation = "verification_operation",

  // #region specific scope types for module OIDC
  // OIDC scope types must be integrated in the global `ScopeTypes` enum
  // because we use `scopedRequestReducer` and its param type is `ScopeType`.
  // @todo oauth maybe there's another way?
  OIDCLoadProviders = OIDCRequestScopes.LoadOIDCProviders,
  OIDCFetchToken = OIDCRequestScopes.FetchOIDCToken,
  OIDCLoginWithToken = OIDCRequestScopes.LoginWithOIDCToken,
  // #endregion
}